/**
 * Deal Underwriter — single-page tool at /underwrite
 *
 * Modes:  For-Sale (Flip)  ·  Rental (Hold)
 *
 * - Inputs on left, live results on right
 * - All math is client-side and re-runs on every keystroke
 * - Save / Load / Convert-to-Project hit the backend deals API
 * - Closing costs toggle: National avg ↔ My actuals
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  computeForSale, computeRental, blankInputs, CONSTRUCTION_CATEGORIES,
  CLOSING_LINE_LABELS, computeSaleClosingCosts, computeBankReadiness,
} from '@/lib/dealMath';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Plus, Save, FolderOpen, Trash2, ArrowRight, Calculator, Building2, Home, AlertTriangle, X, Landmark, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  NumInput, Section, KpiTile, fmt, fmtPct,
} from '@/components/analysis/AnalysisPrimitives';

// NumInput, Section, KpiTile, fmt, fmtPct are imported above so both
// Underwrite and Project Analysis stay in lock-step visually.

// --- Saved deals list (overlay panel) ---
const SavedDealsPanel = ({ open, onClose, userId, onLoad, onDeleted, currentId }) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(`/deals?user_id=${userId}`);
        if (alive) setDeals(res.data || []);
      } catch (e) { /* swallow */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [open, userId]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg">Saved Deals</h2>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {!loading && deals.length === 0 && (
          <p className="text-sm text-slate-500 text-center py-8">No saved deals yet. Build one and click Save.</p>
        )}
        <div className="space-y-2">
          {deals.map((d) => (
            <div key={d.id} className={`border rounded-lg p-3 hover:border-blue-400 cursor-pointer transition-colors ${d.id === currentId ? 'border-blue-500 bg-blue-50' : 'border-slate-200'}`}
                 onClick={() => onLoad(d)} data-testid={`saved-deal-${d.id}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <p className="font-semibold text-sm truncate">{d.name}</p>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${d.mode === 'for_sale' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {d.mode === 'for_sale' ? 'Flip' : 'Hold'}
                </span>
              </div>
              {d.address && <p className="text-[11px] text-slate-500 truncate">{d.address}</p>}
              <div className="flex items-center justify-between text-[11px] text-slate-600 mt-1">
                <span className="font-mono">{d.computed?.netProfit != null ? `Profit: ${fmt(d.computed.netProfit)}` : (d.computed?.irr != null ? `IRR: ${d.computed.irr}%` : '—')}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); if (window.confirm('Delete this deal?')) onDeleted(d.id); }}
                  className="text-red-500 hover:text-red-700"
                  data-testid={`delete-deal-${d.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              {d.linked_project_id && (
                <p className="text-[10px] text-emerald-700 font-medium mt-1">✓ Converted to Project</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function DealUnderwriter({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [dealId, setDealId] = useState(null);
  const [name, setName] = useState('Untitled Deal');
  const [address, setAddress] = useState('');
  const [mode, setMode] = useState('for_sale');
  const [inputs, setInputs] = useState(blankInputs('for_sale'));
  const [showSaved, setShowSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load deal from query string ?id=
  useEffect(() => {
    const idFromUrl = searchParams.get('id');
    if (idFromUrl && idFromUrl !== dealId) {
      (async () => {
        try {
          const res = await api.get(`/deals/${idFromUrl}`);
          loadDeal(res.data);
        } catch (e) {
          toast.error('Could not load deal');
        }
      })();
    }
    // Only re-run when search params actually change.
  }, [searchParams]);

  const loadDeal = (d) => {
    setDealId(d.id);
    setName(d.name || 'Untitled');
    setAddress(d.address || '');
    setMode(d.mode || 'for_sale');
    setInputs({ ...blankInputs(d.mode || 'for_sale'), ...d.inputs });
    setShowSaved(false);
    toast.success('Deal loaded');
  };

  // ---- compute results live ----
  const result = useMemo(() => {
    return mode === 'for_sale' ? computeForSale(inputs) : computeRental(inputs);
  }, [mode, inputs]);

  // ---- Helpers to update nested inputs ----
  const setAcq = (patch) => setInputs((p) => ({ ...p, acquisition: { ...p.acquisition, ...patch } }));
  const setCon = (patch) => setInputs((p) => ({ ...p, construction: { ...p.construction, ...patch } }));
  const setCat = (key, val) => setInputs((p) => ({ ...p, construction: { ...p.construction, categories: { ...p.construction.categories, [key]: val } } }));
  const setFin = (patch) => setInputs((p) => ({ ...p, financing: { ...p.financing, ...patch } }));
  const setSale = (patch) => setInputs((p) => ({ ...p, sale: { ...p.sale, ...patch } }));
  const setRental = (patch) => setInputs((p) => ({ ...p, rental: { ...p.rental, ...patch } }));
  const setOpex = (patch) => setInputs((p) => ({ ...p, rental: { ...p.rental, operating_expenses: { ...p.rental.operating_expenses, ...patch } } }));
  const setBank = (patch) => setInputs((p) => ({ ...p, bank: { ...(p.bank || {}), ...patch } }));

  // ---- Bank readiness verdict (grades the deal against the lender's rules) ----
  const readiness = useMemo(
    () => computeBankReadiness(inputs, result, mode),
    [inputs, result, mode]
  );

  const onModeChange = (newMode) => {
    setMode(newMode);
    setInputs((p) => ({ ...blankInputs(newMode), ...p, mode: newMode }));
  };

  const handleSave = async () => {
    if (!user?.id) { toast.error('Please log in'); return; }
    if (!name.trim()) { toast.error('Name your deal first'); return; }
    setSaving(true);
    try {
      const payload = {
        user_id: user.id,
        name: name.trim(),
        address,
        mode,
        inputs: { ...inputs, units: Number(inputs.units) || 1 },
        computed: result,
      };
      let saved;
      if (dealId) {
        const res = await api.patch(`/deals/${dealId}`, payload);
        saved = res.data;
      } else {
        const res = await api.post('/deals', payload);
        saved = res.data;
        setDealId(saved.id);
        navigate(`/analysis-suite?tab=underwrite&id=${saved.id}`, { replace: true });
      }
      toast.success(dealId ? 'Deal updated' : 'Deal saved');
    } catch (e) {
      toast.error('Save failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/deals/${id}`);
      toast.success('Deleted');
      if (id === dealId) {
        setDealId(null);
        setInputs(blankInputs(mode));
        setName('Untitled Deal');
        setAddress('');
        navigate('/analysis-suite?tab=underwrite', { replace: true });
      }
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleConvert = async () => {
    if (!dealId) {
      toast.error('Save the deal first');
      return;
    }
    if (!window.confirm('Convert this deal to a real Project? It will be added to your Project Hub with the construction budget seeded. You can refine numbers there as estimates come in.')) return;
    try {
      const res = await api.post(`/deals/${dealId}/convert-to-project`);
      const projectId = res.data.project_id;
      toast.success('Deal converted to Project');
      navigate(`/project/${projectId}`);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error('Convert failed: ' + detail);
    }
  };

  const handleNew = () => {
    setDealId(null);
    setName('Untitled Deal');
    setAddress('');
    setInputs(blankInputs(mode));
    navigate('/analysis-suite?tab=underwrite', { replace: true });
  };

  // Convenience: total construction shown in inputs
  const totalConstructionEntered = useMemo(
    () => Object.values(inputs.construction.categories).reduce((a, b) => a + (Number(b) || 0), 0),
    [inputs.construction.categories]
  );

  return (
    <div className="px-4 sm:px-6 py-5 max-w-[1500px] mx-auto" data-testid="deal-underwriter-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-blue-600">Deal Underwriter</p>
          <div className="flex items-center gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="text-xl font-bold text-slate-900 bg-transparent border-b-2 border-transparent focus:border-blue-500 focus:outline-none px-1"
              data-testid="deal-name-input"
            />
          </div>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="Address (optional)"
            className="text-xs text-slate-500 bg-transparent focus:outline-none mt-0.5 px-1 w-full max-w-md"
            data-testid="deal-address-input"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={handleNew} data-testid="new-deal-btn"><Plus className="w-3.5 h-3.5 mr-1" />New</Button>
          <Button variant="outline" size="sm" onClick={() => setShowSaved(true)} data-testid="open-saved-deals-btn"><FolderOpen className="w-3.5 h-3.5 mr-1" />Saved</Button>
          <Button size="sm" onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-deal-btn"><Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving…' : 'Save'}</Button>
          <Button
            size="sm"
            onClick={handleConvert}
            disabled={!dealId}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300"
            title={!dealId ? 'Save the deal first' : 'Convert to Project'}
            data-testid="convert-to-project-btn"
          >
            <ArrowRight className="w-3.5 h-3.5 mr-1" />Convert to Project
          </Button>
        </div>
      </div>

      {/* Mode Tabs */}
      <Tabs value={mode} onValueChange={onModeChange} className="mb-4">
        <TabsList className="grid grid-cols-2 max-w-md">
          <TabsTrigger value="for_sale" data-testid="mode-for-sale"><Home className="w-4 h-4 mr-1" />For-Sale (Flip)</TabsTrigger>
          <TabsTrigger value="rental" data-testid="mode-rental"><Building2 className="w-4 h-4 mr-1" />Rental (Hold)</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* ============================== INPUTS ============================== */}
        <div className="lg:col-span-7 space-y-3">
          {/* Acquisition */}
          <Section title="Acquisition" icon={Home}>
            <div className="grid grid-cols-2 gap-3">
              <NumInput label="Purchase Price (Home/Building)" value={inputs.acquisition.purchase_price_home} onChange={(v) => setAcq({ purchase_price_home: v })} testid="inp-purchase-home" />
              <NumInput label="Purchase Price (Land)" value={inputs.acquisition.purchase_price_land} onChange={(v) => setAcq({ purchase_price_land: v })} testid="inp-purchase-land" />
              <NumInput label="Acquisition Closing Costs" value={inputs.acquisition.closing_costs} onChange={(v) => setAcq({ closing_costs: v })} hint="Title, legal, inspection at purchase" testid="inp-acq-closing" />
              <NumInput label="Insurance (one-time / pre-build)" value={inputs.acquisition.insurance} onChange={(v) => setAcq({ insurance: v })} testid="inp-acq-insurance" />
              <NumInput label="Square Footage" value={inputs.acquisition.sqft} onChange={(v) => setAcq({ sqft: v })} prefix={null} suffix="sqft" testid="inp-sqft" />
              <NumInput label="Number of Units" value={inputs.units} onChange={(v) => setInputs((p) => ({ ...p, units: v }))} prefix={null} suffix="units" testid="inp-units" />
            </div>
          </Section>

          {/* Construction Budget */}
          <Section title="Construction Budget" icon={Calculator}>
            <p className="text-[11px] text-slate-500">Same 8 categories as DrawReady's project tracking — when you Convert to Project these numbers seed the construction budget.</p>
            <div className="grid grid-cols-2 gap-3">
              {CONSTRUCTION_CATEGORIES.map((cat) => (
                <NumInput key={cat.key} label={cat.label} value={inputs.construction.categories[cat.key]} onChange={(v) => setCat(cat.key, v)} hint={cat.hint} testid={`inp-cat-${cat.key}`} />
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 pt-2 border-t">
              <NumInput label="Contingency" value={inputs.construction.contingency} onChange={(v) => setCon({ contingency: v })} hint="Buffer for cost overruns" testid="inp-contingency" />
              <NumInput label="Build Duration" value={inputs.construction.duration_months} onChange={(v) => setCon({ duration_months: v })} prefix={null} suffix="months" testid="inp-duration" />
            </div>
            <div className="bg-slate-50 rounded-md px-3 py-2 flex items-center justify-between text-sm">
              <span className="text-slate-700">Hard cost subtotal</span>
              <span className="font-mono font-semibold">{fmt(totalConstructionEntered)}</span>
            </div>
          </Section>

          {/* Financing */}
          <Section title="Financing — Construction Loan" icon={Building2}>
            <div className="grid grid-cols-2 gap-3">
              <NumInput label="Loan Amount" value={inputs.financing.loan_amount} onChange={(v) => setFin({ loan_amount: v })} testid="inp-loan-amount" />
              <NumInput label="Interest Rate" value={inputs.financing.interest_rate} onChange={(v) => setFin({ interest_rate: v })} prefix={null} suffix="%" testid="inp-interest-rate" />
              <NumInput label="Term" value={inputs.financing.term_months} onChange={(v) => setFin({ term_months: v })} prefix={null} suffix="months" testid="inp-term" />
              <NumInput label="Equity / Cash In" value={inputs.financing.equity} onChange={(v) => setFin({ equity: v })} hint="Auto = Total Cost − Loan if blank" testid="inp-equity" />
            </div>
          </Section>

          {/* MODE-SPECIFIC */}
          {mode === 'for_sale' && (
            <Section title="Sale Assumptions" icon={Home}>
              <div className="grid grid-cols-2 gap-3">
                <NumInput label="Projected Sale Price" value={inputs.sale.sale_price} onChange={(v) => setSale({ sale_price: v })} testid="inp-sale-price" />
                <NumInput label="Months to Sell (post-completion)" value={inputs.sale.months_to_sell} onChange={(v) => setSale({ months_to_sell: v })} prefix={null} suffix="months" testid="inp-months-to-sell" />
              </div>

              {/* Closing costs toggle */}
              <div className="border rounded-md p-3 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-slate-700">Selling-Side Closing Costs</p>
                  <div className="flex bg-white border rounded-md text-[11px] overflow-hidden">
                    <button
                      type="button"
                      className={`px-2.5 py-1 ${inputs.sale.use_avg_closing ? 'bg-blue-600 text-white' : 'text-slate-600'}`}
                      onClick={() => setSale({ use_avg_closing: true })}
                      data-testid="closing-mode-avg"
                    >
                      National avg
                    </button>
                    <button
                      type="button"
                      className={`px-2.5 py-1 ${!inputs.sale.use_avg_closing ? 'bg-blue-600 text-white' : 'text-slate-600'}`}
                      onClick={() => {
                        // Pre-fill with current avg numbers as starting point
                        const avg = computeSaleClosingCosts(inputs.sale.sale_price, { use_avg_closing: true }).lines;
                        setSale({ use_avg_closing: false, closing_overrides: { ...avg } });
                      }}
                      data-testid="closing-mode-actual"
                    >
                      My actual costs
                    </button>
                  </div>
                </div>
                {inputs.sale.use_avg_closing ? (
                  <div className="text-[11px] text-slate-600 grid grid-cols-2 gap-1">
                    {Object.entries(result.closing?.lines || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between bg-white px-2 py-1 rounded border">
                        <span>{CLOSING_LINE_LABELS[k] || k}</span>
                        <span className="font-mono">{fmt(v)}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    {Object.keys(CLOSING_LINE_LABELS).map((k) => (
                      <NumInput
                        key={k}
                        label={CLOSING_LINE_LABELS[k]}
                        value={inputs.sale.closing_overrides[k] ?? 0}
                        onChange={(v) => setSale({ closing_overrides: { ...inputs.sale.closing_overrides, [k]: v } })}
                        testid={`inp-closing-${k}`}
                      />
                    ))}
                  </div>
                )}
                <div className="flex justify-between mt-2 pt-2 border-t text-sm">
                  <span className="font-medium">Total selling costs</span>
                  <span className="font-mono font-bold">{fmt(result.closing?.total || 0)}</span>
                </div>
              </div>
            </Section>
          )}

          {mode === 'rental' && (
            <>
              <Section title="Rental Income" icon={Building2}>
                <div className="grid grid-cols-2 gap-3">
                  <NumInput label="Monthly Rent / Unit" value={inputs.rental.monthly_rent_per_unit} onChange={(v) => setRental({ monthly_rent_per_unit: v })} testid="inp-monthly-rent" />
                  <NumInput label="Annual Rent Growth" value={inputs.rental.annual_rent_growth_pct} onChange={(v) => setRental({ annual_rent_growth_pct: v })} prefix={null} suffix="%" testid="inp-rent-growth" />
                  <NumInput label="Vacancy Rate" value={inputs.rental.vacancy_pct} onChange={(v) => setRental({ vacancy_pct: v })} prefix={null} suffix="%" testid="inp-vacancy" />
                  <NumInput label="Hold Period" value={inputs.rental.hold_years} onChange={(v) => setRental({ hold_years: v })} prefix={null} suffix="years" testid="inp-hold-years" />
                </div>

                {/* Other income lines */}
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-xs font-semibold text-slate-700">Other Income (e.g., TIF, parking, laundry, storage)</p>
                    <Button size="sm" variant="outline" onClick={() => setRental({ other_income: [...(inputs.rental.other_income || []), { label: '', monthly_amount: 0 }] })} data-testid="add-other-income"><Plus className="w-3 h-3 mr-1" />Add line</Button>
                  </div>
                  {(inputs.rental.other_income || []).length === 0 && (
                    <p className="text-[11px] text-slate-500 italic">No other income lines. TIF, parking, laundry, etc. go here.</p>
                  )}
                  {(inputs.rental.other_income || []).map((line, idx) => (
                    <div key={idx} className="flex gap-2 items-end mb-1.5">
                      <div className="flex-1">
                        <Input placeholder="Label (e.g., TIF reimbursement)" value={line.label} onChange={(e) => {
                          const arr = [...inputs.rental.other_income]; arr[idx] = { ...arr[idx], label: e.target.value }; setRental({ other_income: arr });
                        }} className="h-8 text-sm" data-testid={`other-income-label-${idx}`} />
                      </div>
                      <div className="w-32">
                        <Input type="number" placeholder="Monthly $" value={line.monthly_amount ?? ''} onChange={(e) => {
                          const arr = [...inputs.rental.other_income]; arr[idx] = { ...arr[idx], monthly_amount: Number(e.target.value) }; setRental({ other_income: arr });
                        }} className="h-8 text-sm font-mono" data-testid={`other-income-amount-${idx}`} />
                      </div>
                      <button onClick={() => {
                        const arr = [...inputs.rental.other_income]; arr.splice(idx, 1); setRental({ other_income: arr });
                      }} className="text-red-500 mb-2" data-testid={`other-income-del-${idx}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              </Section>

              <Section title="Operating Expenses (annual)" icon={Calculator}>
                <div className="grid grid-cols-2 gap-3">
                  <NumInput label="Property Mgmt" value={inputs.rental.operating_expenses.property_management_pct} onChange={(v) => setOpex({ property_management_pct: v })} prefix={null} suffix="% of EGI" testid="inp-pm" />
                  <NumInput label="Cap Repair Fund" value={inputs.rental.operating_expenses.cap_repair_fund_pct} onChange={(v) => setOpex({ cap_repair_fund_pct: v })} prefix={null} suffix="% of EGI" testid="inp-cap-repair" />
                  <NumInput label="Property Tax / unit / yr" value={inputs.rental.operating_expenses.property_tax_per_unit_yr} onChange={(v) => setOpex({ property_tax_per_unit_yr: v })} testid="inp-prop-tax" />
                  <NumInput label="Insurance / yr" value={inputs.rental.operating_expenses.insurance_yr} onChange={(v) => setOpex({ insurance_yr: v })} testid="inp-insurance-yr" />
                  <NumInput label="Accounting / yr" value={inputs.rental.operating_expenses.accounting_yr} onChange={(v) => setOpex({ accounting_yr: v })} testid="inp-accounting" />
                  <NumInput label="Advertising / yr" value={inputs.rental.operating_expenses.advertising_yr} onChange={(v) => setOpex({ advertising_yr: v })} testid="inp-advertising" />
                  <NumInput label="Maintenance / yr" value={inputs.rental.operating_expenses.maintenance_yr} onChange={(v) => setOpex({ maintenance_yr: v })} testid="inp-maintenance" />
                  <NumInput label="Utilities / yr (owner-paid)" value={inputs.rental.operating_expenses.utilities_yr} onChange={(v) => setOpex({ utilities_yr: v })} testid="inp-utilities" />
                </div>
              </Section>

              <Section title="Permanent Financing & Exit" icon={Building2}>
                <div className="grid grid-cols-2 gap-3">
                  <NumInput label="Perm Loan Amount" value={inputs.rental.perm_loan_amount} onChange={(v) => setRental({ perm_loan_amount: v })} hint="After construction conversion" testid="inp-perm-loan" />
                  <NumInput label="Perm Interest Rate" value={inputs.rental.perm_interest_rate} onChange={(v) => setRental({ perm_interest_rate: v })} prefix={null} suffix="%" testid="inp-perm-rate" />
                  <NumInput label="Perm Term" value={inputs.rental.perm_term_years} onChange={(v) => setRental({ perm_term_years: v })} prefix={null} suffix="years" testid="inp-perm-term" />
                  <NumInput label="Exit Cap Rate" value={inputs.rental.exit_cap_rate} onChange={(v) => setRental({ exit_cap_rate: v })} prefix={null} suffix="%" hint="Higher = lower sale price" testid="inp-exit-cap" />
                  <NumInput label="Selling Costs at Exit" value={inputs.rental.selling_costs_pct} onChange={(v) => setRental({ selling_costs_pct: v })} prefix={null} suffix="% of sale" testid="inp-selling-costs" />
                </div>
              </Section>
            </>
          )}
        </div>

        {/* ============================== RESULTS ============================== */}
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-4 space-y-3">

            {/* Bank Readiness verdict — the 30-second "take it to the bank?" answer */}
            <Card className="p-4 border-2" data-testid="bank-readiness-panel">
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2">
                  <Landmark className="w-4 h-4 text-slate-500" />
                  <h3 className="text-sm font-bold text-slate-800">
                    Bank Readiness{inputs.bank?.name ? ` · ${inputs.bank.name}` : ''}
                  </h3>
                </div>
                {readiness.verdict === 'ready' && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid="verdict-badge">
                    <CheckCircle2 className="w-3.5 h-3.5" /> On target
                  </span>
                )}
                {readiness.verdict === 'needs_work' && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-amber-100 text-amber-700" data-testid="verdict-badge">
                    <AlertTriangle className="w-3.5 h-3.5" /> Needs work
                  </span>
                )}
                {readiness.verdict === 'incomplete' && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500" data-testid="verdict-badge">
                    Fill in numbers
                  </span>
                )}
              </div>
              <p className="text-[11px] text-slate-500 mb-3">
                Enter what your bank looks for — knowing their targets up front is how you set your deal up to succeed.
              </p>

              {/* Inline "what your bank looks for" inputs (merged in — no separate section) */}
              <div className="bg-slate-50 rounded-lg p-3 mb-3 space-y-2">
                <div>
                  <label className="text-[11px] font-medium text-slate-600">Bank / Lender name (optional)</label>
                  <Input
                    value={inputs.bank?.name || ''}
                    onChange={(e) => setBank({ name: e.target.value })}
                    placeholder="e.g. First National"
                    className="mt-1 h-8 text-sm bg-white"
                    data-testid="inp-bank-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <NumInput label="Max Loan-to-Cost" value={inputs.bank?.max_ltc_pct} onChange={(v) => setBank({ max_ltc_pct: v })} prefix={null} suffix="%" testid="inp-bank-ltc" />
                  <NumInput label="Max Loan-to-Value" value={inputs.bank?.max_ltv_pct} onChange={(v) => setBank({ max_ltv_pct: v })} prefix={null} suffix="%" hint="vs ARV" testid="inp-bank-ltv" />
                  <NumInput label="Min Your Equity" value={inputs.bank?.min_equity_pct} onChange={(v) => setBank({ min_equity_pct: v })} prefix={null} suffix="%" testid="inp-bank-equity" />
                  {mode === 'rental' ? (
                    <NumInput label="Target DSCR" value={inputs.bank?.target_dscr} onChange={(v) => setBank({ target_dscr: v })} prefix={null} suffix="x" hint="e.g. 1.10" testid="inp-bank-dscr" />
                  ) : (
                    <NumInput label="Min Profit Margin" value={inputs.bank?.min_profit_margin_pct} onChange={(v) => setBank({ min_profit_margin_pct: v })} prefix={null} suffix="%" testid="inp-bank-margin" />
                  )}
                </div>
              </div>

              {readiness.total > 0 && (
                <p className="text-xs text-slate-500 mb-2">
                  {readiness.passing} of {readiness.total} targets met · cash to close ≈ <span className="font-semibold text-slate-700">{fmt(readiness.cashToClose)}</span>
                </p>
              )}
              <div className="space-y-1.5">
                {readiness.metrics.map((m) => (
                  <div key={m.key} className="flex items-center justify-between text-xs py-1 border-b border-slate-50 last:border-0" data-testid={`readiness-${m.key}`}>
                    <div className="min-w-0">
                      <span className="font-medium text-slate-700">{m.label}</span>
                      <span className="text-slate-400 ml-1.5">{m.rule}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`font-mono font-semibold ${m.ok === null ? 'text-slate-400' : m.ok ? 'text-emerald-700' : 'text-amber-700'}`}>
                        {m.value == null ? '—' : (m.unit === 'x' ? m.value.toFixed(2) + 'x' : m.value.toFixed(1) + '%')}
                      </span>
                      {m.ok === true && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                      {m.ok === false && <XCircle className="w-4 h-4 text-amber-500" />}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">
                Banks are transparent about these targets — this fast screen shows where you stand so you can strengthen the deal before you apply. Final terms still depend on the full review.
              </p>
            </Card>

            {mode === 'for_sale' ? (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <KpiTile label="Net Profit" value={fmt(result.netProfit)} color={result.netProfit >= 0 ? 'emerald' : 'red'} />
                  <KpiTile label="ROI on Equity" value={fmtPct(result.roi)} color="blue" />
                  <KpiTile label="Annualized ROI" value={fmtPct(result.annualizedRoi)} color="blue" />
                  <KpiTile label="IRR" value={fmtPct(result.irr)} color="amber" />
                </div>

                <Card className="p-4">
                  <h3 className="text-sm font-semibold mb-2">Sources & Uses</h3>
                  <table className="w-full text-xs">
                    <tbody>
                      <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600">Acquisition Total</td><td className="py-1.5 text-right font-mono">{fmt(result.acquisitionTotal)}</td></tr>
                      <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600">Construction (hard)</td><td className="py-1.5 text-right font-mono">{fmt(result.constructionHard)}</td></tr>
                      <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600">Contingency</td><td className="py-1.5 text-right font-mono">{fmt(result.contingency)}</td></tr>
                      <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600">Carry Interest (build + sell)</td><td className="py-1.5 text-right font-mono">{fmt(result.carryInterest)}</td></tr>
                      <tr className="border-t-2 border-slate-300"><td className="py-1.5 font-semibold">Total Project Cost</td><td className="py-1.5 text-right font-mono font-bold">{fmt(result.totalProjectCost)}</td></tr>
                      <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600 pt-3">Sale Price</td><td className="py-1.5 text-right font-mono">{fmt(result.salePrice)}</td></tr>
                      <tr className="border-b border-slate-100"><td className="py-1.5 text-slate-600">− Selling costs</td><td className="py-1.5 text-right font-mono text-red-700">−{fmt(result.closing?.total || 0)}</td></tr>
                      <tr><td className="py-1.5 font-semibold">Net Sale Proceeds</td><td className="py-1.5 text-right font-mono font-bold">{fmt(result.netSaleProceeds)}</td></tr>
                      <tr className="border-t-2 border-slate-400"><td className="py-2 font-bold">Net Profit</td><td className={`py-2 text-right font-mono font-bold ${result.netProfit >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(result.netProfit)}</td></tr>
                    </tbody>
                  </table>
                </Card>

                {result.netProfit < 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3 flex gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-red-800">This deal currently shows a <strong>loss</strong>. Adjust sale price, build costs, or financing to find your breakeven.</p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  <KpiTile label="Y1 Cash Flow" value={fmt(result.year1CashFlow)} color={result.year1CashFlow >= 0 ? 'emerald' : 'red'} />
                  <KpiTile label="Y1 DSCR" value={result.year1DSCR == null ? '—' : result.year1DSCR.toFixed(2) + 'x'} color={result.year1DSCR >= 1.2 ? 'emerald' : 'amber'} sub="Bank target ≥ 1.20x" />
                  <KpiTile label="IRR (lev)" value={fmtPct(result.irr)} color="blue" />
                  <KpiTile label="Equity Multiple" value={result.equityMultiple ? result.equityMultiple.toFixed(2) + 'x' : '—'} color="amber" />
                  <KpiTile label="Avg Cash-on-Cash" value={fmtPct(result.avgCashOnCash)} color="blue" />
                  <KpiTile label="Exit Sale Value" value={fmt(result.exitValue)} color="slate" sub={`After ${inputs.rental.hold_years} yrs`} />
                </div>

                <Card className="p-3">
                  <h3 className="text-sm font-semibold mb-2">Annual Pro Forma</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[10px]">
                      <thead>
                        <tr className="border-b-2 border-slate-300 text-slate-600">
                          <th className="py-1 px-1 text-left">Yr</th>
                          <th className="py-1 px-1 text-right">EGI</th>
                          <th className="py-1 px-1 text-right">OpEx</th>
                          <th className="py-1 px-1 text-right">NOI</th>
                          <th className="py-1 px-1 text-right">Debt</th>
                          <th className="py-1 px-1 text-right">CF</th>
                          <th className="py-1 px-1 text-right">DSCR</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        {result.years.map((y) => (
                          <tr key={y.year} className="border-b border-slate-100">
                            <td className="py-1 px-1 font-bold">{y.year}</td>
                            <td className="py-1 px-1 text-right">{fmt(y.egi)}</td>
                            <td className="py-1 px-1 text-right text-red-700">−{fmt(y.total_opex)}</td>
                            <td className="py-1 px-1 text-right">{fmt(y.noi)}</td>
                            <td className="py-1 px-1 text-right text-red-700">−{fmt(y.debt_service)}</td>
                            <td className={`py-1 px-1 text-right font-bold ${y.cash_flow >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>{fmt(y.cash_flow)}</td>
                            <td className={`py-1 px-1 text-right ${y.dscr >= 1.2 ? 'text-emerald-700' : 'text-amber-700'}`}>{y.dscr == null ? '—' : y.dscr.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>

                <Card className="p-4">
                  <h3 className="text-sm font-semibold mb-2">Exit Summary</h3>
                  <table className="w-full text-xs">
                    <tbody>
                      <tr className="border-b border-slate-100"><td className="py-1 text-slate-600">Exit Sale Value</td><td className="py-1 text-right font-mono">{fmt(result.exitValue)}</td></tr>
                      <tr className="border-b border-slate-100"><td className="py-1 text-slate-600">− Selling costs</td><td className="py-1 text-right font-mono text-red-700">−{fmt(result.sellingCosts)}</td></tr>
                      <tr className="border-b border-slate-100"><td className="py-1 text-slate-600">− Loan balance (approx)</td><td className="py-1 text-right font-mono text-red-700">−{fmt(result.loanBalanceApprox)}</td></tr>
                      <tr className="border-t-2 border-slate-400"><td className="py-1.5 font-semibold">Net Exit Proceeds</td><td className="py-1.5 text-right font-mono font-bold text-emerald-700">{fmt(result.netExitProceeds)}</td></tr>
                    </tbody>
                  </table>
                </Card>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Save panel */}
      <SavedDealsPanel
        open={showSaved}
        onClose={() => setShowSaved(false)}
        userId={user?.id}
        currentId={dealId}
        onLoad={loadDeal}
        onDeleted={handleDelete}
      />
    </div>
  );
}
